import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, PencilSimple, Trash, Copy, Calculator, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const UNITS = ["per unit", "per metre", "per sq ft", "per application", "per hour", "lump sum"];
const EMPTY_ITEM = { product_category: "", section: "Charges", name: "", description: "", rate: "", gst_percent: "", gst_required: false, unit: "per unit", active: true, display_order: 0 };

export default function EstimateCalculatorAdmin() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [newCat, setNewCat] = useState("");
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [cfgBusy, setCfgBusy] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [editId, setEditId] = useState(null);

  const loadCats = useCallback(() => {
    api.get("/estimate-categories").then((r) => {
      setCategories(r.data);
      setCategory((c) => c || r.data[0] || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { loadCats(); }, [loadCats]);

  const loadCategory = useCallback(() => {
    if (!category) { setItems([]); setConfig(null); return; }
    api.get("/estimate-items", { params: { category } }).then((r) => setItems(r.data)).catch(() => {});
    api.get("/estimate-config", { params: { category } }).then((r) => setConfig(r.data)).catch(() => {});
  }, [category]);
  useEffect(() => { loadCategory(); }, [loadCategory]);

  const addCategory = () => {
    const c = newCat.trim();
    if (!c) return;
    if (!categories.includes(c)) setCategories((cs) => [...cs, c]);
    setCategory(c); setNewCat("");
    setConfig({ product_category: c, fixed_charge: 0, fixed_charge_label: "One-time charge", gst_percent: 0, intro_text: "", active: true });
  };

  const saveConfig = async () => {
    setCfgBusy(true);
    try {
      const { data } = await api.put("/estimate-config", {
        product_category: category,
        fixed_charge: Number(config.fixed_charge) || 0,
        fixed_charge_label: config.fixed_charge_label || "One-time charge",
        gst_percent: Number(config.gst_percent) || 0,
        fixed_gst_required: config.fixed_gst_required !== false,
        intro_text: config.intro_text || "", active: config.active !== false,
      });
      setConfig(data); toast.success("Settings saved");
      loadCats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCfgBusy(false); }
  };

  const openNewItem = () => { setEditId(null); setItemForm({ ...EMPTY_ITEM, product_category: category }); setItemOpen(true); };
  const openEditItem = (it) => { setEditId(it.id); setItemForm({ ...it }); setItemOpen(true); };

  const saveItem = async () => {
    if (!itemForm.name || !category) { toast.error("Item name is required"); return; }
    const payload = { ...itemForm, product_category: category, rate: Number(itemForm.rate) || 0,
      gst_percent: (itemForm.gst_percent === "" || itemForm.gst_percent === null) ? null : Number(itemForm.gst_percent),
      display_order: Number(itemForm.display_order) || 0 };
    try {
      if (editId) await api.put(`/estimate-items/${editId}`, payload);
      else await api.post("/estimate-items", payload);
      toast.success(editId ? "Item updated" : "Item added");
      setItemOpen(false); loadCategory(); loadCats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delItem = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await api.delete(`/estimate-items/${it.id}`); toast.success("Deleted"); loadCategory(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shareLink = `${window.location.origin}/estimate`;
  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Link copied"); };

  const exportItems = async () => {
    try {
      const res = await api.get("/estimate-items/export", { params: { category: category || "" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `estimate_items_${(category || "all").replace(/ /g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const importItems = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/estimate-items/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      loadCats(); loadCategory();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import failed"); }
  };

  return (
    <>
      <PageHeader title="Estimate Calculator" subtitle="Manage the public fair-price calculator (rates, one-time charge, GST)"
        actions={
          <div className="flex items-center gap-2">
            <label>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importItems} data-testid="est-import-input" />
              <Button asChild variant="outline" size="sm" className="rounded-sm gap-2">
                <span className="cursor-pointer" data-testid="est-import-btn"><UploadSimple size={15} weight="bold" /> Import Excel</span>
              </Button>
            </label>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={exportItems} data-testid="est-export-btn">
              <DownloadSimple size={15} weight="bold" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={copyLink} data-testid="copy-estimate-link">
              <Copy size={15} weight="bold" /> Copy public link
            </Button>
          </div>
        } />
      <div className="p-8 space-y-6">
        <Card className="rounded-sm p-5 border-border">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="label-caps">Service Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-sm mt-1.5 w-64" data-testid="est-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="label-caps">Add new category</Label>
                <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Refrigerator" className="rounded-sm mt-1.5 w-48" data-testid="est-new-category-input"
                  onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }} />
              </div>
              <Button variant="outline" className="rounded-sm gap-1.5" onClick={addCategory} data-testid="est-add-category"><Plus size={15} weight="bold" /> Add</Button>
            </div>
            <div className="ml-auto text-xs text-muted-foreground">
              Public link: <a href={shareLink} target="_blank" rel="noreferrer" className="text-primary font-mono">{shareLink}</a>
              <div>Per-order: <span className="font-mono">{shareLink}?id=&lt;order id&gt;</span></div>
            </div>
          </div>
        </Card>

        {config && (
          <Card className="rounded-sm p-5 border-border" data-testid="est-config-card">
            <div className="flex items-center gap-2 mb-4"><Calculator size={18} weight="bold" className="text-primary" /><h3 className="font-heading font-semibold">Charges for {category}</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label className="label-caps">One-time charge (₹)</Label>
                <Input type="number" min={0} value={config.fixed_charge} onChange={(e) => setConfig({ ...config, fixed_charge: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-fixed-charge" /></div>
              <div><Label className="label-caps">Charge label</Label>
                <Input value={config.fixed_charge_label} onChange={(e) => setConfig({ ...config, fixed_charge_label: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-fixed-label" /></div>
              <div><Label className="label-caps">GST %</Label>
                <Input type="number" min={0} value={config.gst_percent} onChange={(e) => setConfig({ ...config, gst_percent: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-gst" /></div>
              <div className="flex items-end"><Button className="rounded-sm w-full" onClick={saveConfig} disabled={cfgBusy} data-testid="est-save-config">{cfgBusy ? "Saving…" : "Save Settings"}</Button></div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Switch checked={config.fixed_gst_required !== false} onCheckedChange={(v) => setConfig({ ...config, fixed_gst_required: v })} data-testid="est-fixed-gst-required" />
              <span className="text-sm">GST on the one-time charge is <strong>mandatory</strong> <span className="text-xs text-muted-foreground">(if off, it's included in the customer's optional-GST tick)</span></span>
            </div>
            <div className="mt-4"><Label className="label-caps">Intro text (shown on the public page)</Label>
              <Textarea value={config.intro_text} onChange={(e) => setConfig({ ...config, intro_text: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-intro" /></div>
          </Card>
        )}

        {category && (
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold">Rate Items ({items.length})</h3>
            <Button className="rounded-sm gap-2" onClick={openNewItem} data-testid="est-add-item"><Plus size={16} weight="bold" /> Add Item</Button>
          </div>
        )}

        {category && (
          <div className="border border-border rounded-sm bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Section</TableHead><TableHead>Item</TableHead>
                  <TableHead className="text-right">Rate</TableHead><TableHead className="text-right">GST %</TableHead><TableHead>Unit</TableHead>
                  <TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="est-items-table">
                {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No items yet. Click "Add Item".</TableCell></TableRow>}
                {items.map((it) => (
                  <TableRow key={it.id} className="hover:bg-secondary/30">
                    <TableCell className="text-sm text-muted-foreground">{it.section}</TableCell>
                    <TableCell><div className="font-medium">{it.name}</div>{it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}</TableCell>
                    <TableCell className="text-right font-mono">₹{it.rate}</TableCell>
                    <TableCell className="text-right font-mono" data-testid={`est-gst-${it.id}`}>
                      {(it.gst_percent === null || it.gst_percent === undefined) ? <span className="text-xs text-muted-foreground" title="Uses category default GST">default</span> : `${it.gst_percent}%`}
                      {it.gst_required ? <span className="block text-[10px] font-bold uppercase text-amber-600">mandatory</span> : <span className="block text-[10px] uppercase text-muted-foreground">optional</span>}
                    </TableCell>
                    <TableCell className="text-sm">{it.unit}</TableCell>
                    <TableCell>{it.active ? <span className="text-xs text-green-600 font-semibold">Active</span> : <span className="text-xs text-muted-foreground">Off</span>}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openEditItem(it)} data-testid={`est-edit-${it.id}`}><PencilSimple size={15} /></Button>
                      <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={() => delItem(it)} data-testid={`est-del-${it.id}`}><Trash size={15} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent data-testid="est-item-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Item" : "Add Item"} · {category}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="label-caps">Item Name *</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-item-name" /></div>
            <div><Label className="label-caps">Section (group)</Label>
              <Input value={itemForm.section} onChange={(e) => setItemForm({ ...itemForm, section: e.target.value })} placeholder="e.g. Installation Supply & Charges" className="rounded-sm mt-1.5" data-testid="est-item-section" /></div>
            <div><Label className="label-caps">Description</Label>
              <Input value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-item-desc" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="label-caps">Rate (₹) *</Label>
                <Input type="number" min={0} value={itemForm.rate} onChange={(e) => setItemForm({ ...itemForm, rate: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-item-rate" /></div>
              <div><Label className="label-caps">GST %</Label>
                <Input type="number" min={0} step="0.01" value={itemForm.gst_percent ?? ""} placeholder="Default" onChange={(e) => setItemForm({ ...itemForm, gst_percent: e.target.value })} className="rounded-sm mt-1.5" data-testid="est-item-gst" /></div>
              <div><Label className="label-caps">Unit</Label>
                <Select value={itemForm.unit} onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="est-item-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">Leave GST % blank to use the category default ({config?.gst_percent || 0}%). Set 0 for a GST-free item.</p>
            <div className="flex items-center gap-6 pt-1">
              <div className="flex items-center gap-2">
                <Switch checked={itemForm.gst_required} onCheckedChange={(v) => setItemForm({ ...itemForm, gst_required: v })} data-testid="est-item-gst-required" />
                <span className="text-sm">GST mandatory <span className="text-xs text-muted-foreground">(always charged; if off, customer chooses)</span></span>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={itemForm.active} onCheckedChange={(v) => setItemForm({ ...itemForm, active: v })} data-testid="est-item-active" />
              <span className="text-sm">Active (shown on the public calculator)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setItemOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={saveItem} data-testid="est-item-save">{editId ? "Save" : "Add Item"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
